import { Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { Injectable } from '@angular/core'
import { AuthService } from '@appService/auth.service'
import { OptionModel } from '../../../projects/vss-ui/src/vss-ui/vss-ui.model'
import { TreeHelper } from '../../../projects/vss-ui/src/vss-ui/helper/tree-helper'
import { PositionService } from '@appService/admin/position.service'
import { MappingRoleService } from '@appService/admin/mapping-role.service'
import { uniqBy } from 'lodash'
import { SystemParamatersService } from '@app/service/admin/system-paramaters.service'
import { PARAMETER } from '@app/modules/shares/enum/common.enum'
import { convertStringJSONToArray, convertStringJSONToArray2 } from '@app/modules/shares/utils/functions/converter'
import { CategoryAcademicYearsService } from '@app/service/category/category-academic-year.service'
import { CoachManageService } from '@app/service/user/coach-manage.service'
import { OrganizationService } from '@app/service/admin/organization.service'
import { ClubService } from '@app/service/category/club.service'

@Injectable({
  providedIn: 'root'
})
export class VssUiConfigService {
  constructor(
    private positionService: PositionService,
    private mappingRoleService: MappingRoleService,
    private authService: AuthService,
    private systemParamatersService: SystemParamatersService,
    private categoryAcademicYearsService: CategoryAcademicYearsService,
    private coachService: CoachManageService,
    private organizationService: OrganizationService,
    private clubService: ClubService,
  ) {
  }

  getUserStream = (params: any): Observable<OptionModel[]> => {
    return this.authService.searchStaffsCustom({
      page: params?.page, size: params?.size
    }, {
      ...params,
      sortField: [{ fieldName: 'full_name', sort: 'asc' }]
    })
      .pipe(
        map(res => {
          return res.data?.content?.map((x: any) => ({
            ...x,
            value: x.userName,
            label: `${x.userName} - ${x.fullName}`,
            totalPages: res.data.totalPages,
          })).sort((a: any, b: any) => a.userName.localeCompare(b.userName))
        })
      )
  }

  getDefaultUsers = (values: any) => {
    return this.authService.searchStaffsCustom({ page: 1, size: 10000 }, { usernameList: values })
      .pipe(
        map(res => res.data?.content?.map((x: any) => ({
          ...x,
          value: x.userName,
          label: `${x.userName} - ${x.fullName}`,
          totalPages: res.data.totalPages,
        })) || [])
      )
  }

  getDepartmentList = (params: any): Observable<any[]> => {
    return this.authService.postDepartmentList(
      params, { ...params, sortField: [] }
    ).pipe(
      map(
        resp => {
          const data = (resp?.data?.content || resp?.data)?.map((x: any) => ({
            ...x,
            value: x.id,
            totalPages: resp.data.totalPages,
            label: `${x.organizationCode} - ${x.organizationName}`
          })).sort((a: any, b: any) => a.organizationName.localeCompare(b.organizationName)) || []
          return TreeHelper.parseDataToNodes(
            data.map(
              (r: any) => ({ ...r, title: `${r.organizationCode} - ${r.organizationName}`, })
            ), true,
          )
        }
      )
    )
  }

  getDefaultDepartmentList = (values: any) => {
    return this.authService.getListDepartmentByIds(values)
      .pipe(
        map(res => res.data?.map((x: any) => ({
          ...x,
          value: x.id,
          label: `${x.organizationCode} - ${x.organizationName}`,
          totalPages: res.data.totalPages
        })) || [])
      )
  }



  getPositionByRole = (roleCode: string) => {
    return this.mappingRoleService.getPositionByRole(roleCode)
      .pipe(
        switchMap(
          resp => {
            return (resp?.data?.content || resp?.data)?.length ? of({
              ...resp,
              data: (resp?.data?.content || resp?.data).map((d: any) => ({ ...d, idMap: d.id, id: d.positionId }))
            })
              : this.getPositions()
          }
        )
      )
  }

  getPositions = () => {
    return this.positionService.postPositionList(
      {
        page: 1,
        size: 10000,
        lsCondition: [],
        sortField: []
      }
    )
  }

  getPositionForSelector = (params: any): Observable<any[]> => {
    return (
      params?.staffRole && !Array.isArray(params?.staffRole)
        ? this.getPositionByRole(params?.staffRole)
        : this.getPositions()
    ).pipe(
      map(resp => (resp?.data?.content || resp?.data)?.map((x: any) => ({
        ...x,
        value: x.id,
        label: `${x.positionCode} - ${x.positionName}`,
        totalPages: resp.data.totalPages,
      })) || [])
    )
  }

  getRoleByPosition = (positionId: number) => {
    return this.mappingRoleService.getRoleByPosition(positionId)
      .pipe(
        switchMap(
          resp => {
            return (resp?.data?.content || resp?.data)?.length ? of(resp) : this.getRoles()
          }
        )
      )
  }

  getRoles = () => {
    return this.mappingRoleService.searchApi(
      { page: 1, size: 10000, lsCondition: [{ operator: 'EQUAL', property: 'isDeleted', propertyType: 'boolean', value: false }], sortField: [] }
    )
  }

  getStaffRole = (params: any): Observable<any[]> => {
    return (
      params?.positionId && !Array.isArray(params?.positionId)
        ? this.getRoleByPosition(params?.positionId)
        : this.getRoles()
    ).pipe(
      map((resp: any) => (uniqBy(resp?.data?.content || resp?.data, 'roleCode'))?.map((x: any) => ({
        ...x,
        value: x.roleCode,
        label: `${x.roleCode}`
      })) || [])
    ) as Observable<any[]>
  }

  getTournament = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let typeList = []
          typeList = resp.items?.find((item: any) => item.code === PARAMETER.TOURNAMENT_TYPE)?.value || []
          typeList = convertStringJSONToArray2(typeList)

          return typeList
        }
      )
    )
  }

  getAwardType = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let typeList = []
          typeList = resp.items?.find((item: any) => item.code === PARAMETER.AWARD_TYPE)?.value || []
          typeList = convertStringJSONToArray2(typeList)
          return typeList
        }
      )
    )
  }

  getLessonType = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let typeList = []
          typeList = resp.items?.find((item: any) => item.code === PARAMETER.LESSON_TYPE)?.value || []
          typeList = convertStringJSONToArray(typeList)
          return typeList
        }
      )
    )
  }

  getClass = (params: any): Observable<any[]> => {

    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let list = resp?.item?.find((item: any) => item.code === PARAMETER.CLASS)?.value || []
          return convertStringJSONToArray(list).map((item: any) => {
            return {
              value: item.value,
              label: item.label
            }
          }) || []
        }
      )
    )
  }

  getUnit = (params: any): Observable<OptionModel[]> => {
    return this.systemParamatersService.apiSearch({
      code: 'UNIT'
    })
      .pipe(
        map((res: any) => {
          return JSON.parse(res?.items[0]?.value)?.map((x: any) => ({ value: x?.code, label: x?.description })) || []
        })
      )
  }
  getTeam = (params: any): Observable<OptionModel[]> => {
    return this.systemParamatersService.apiSearch({
      code: 'TEAM'
    })
      .pipe(
        map((res: any) => {
          return JSON.parse(res?.items[0]?.value)?.map((x: any) => ({ value: x?.value, label: x?.label })) || []
        })
      )
  }

  getSchoolYear = (params: any): Observable<OptionModel[]> => {
    return this.categoryAcademicYearsService.apiSearch({
      page: params?.page, size: params?.size
    })
      .pipe(
        map((res: any) => {
          return res.items?.map((x: any) => ({
            ...x,
            value: [x.startYear, x.endYear],
            label: `${x.startYear} - ${x.endYear}`,
            totalPages: res.data?.totalPages,
          }))
        })
      )
  }

  getAcademicYear = (params: any): Observable<OptionModel[]> => {
    return this.categoryAcademicYearsService.getDropdown({

    })
      .pipe(
        map((res: any) => {
          return res.items?.map((x: any) => ({
            ...x,
            value: x.id,
            label: `${x.startYear} - ${x.endYear}`,
          }))
        })
      )
  }

  getGender = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let list = []
          list = resp.items?.find((item: any) => item.code === PARAMETER.GENDER)?.value || []
          list = convertStringJSONToArray2(list)
          return list
        }
      )
    )
  }
  getTitle = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let list = []
          list = resp.items?.find((item: any) => item.code === PARAMETER.TITLE)?.value || []
          list = convertStringJSONToArray(list)
          return list
        }
      )
    )
  }

  getCoach = (params: any): Observable<any[]> => {

    return this.coachService.search({
      name: params?.keywords || null,
      page: params?.page,
      size: params?.size,
    }).pipe(
      map(
        resp => {
          return resp?.items?.map((item: any) => {
            return {
              value: item.id,
              label: `${item.fullName}`,
              totalPages: item.totalPages
            }
          }) || []
        }
      )
    )
  }

  getClub = (params: any): Observable<any[]> => {

    return this.clubService.search({
      name: params?.keywords || null,
      page: params?.page,
      size: params?.size,
    }).pipe(
      map(
        resp => {
          return resp?.items?.map((item: any) => {
            return {
              value: item.id,
              label: `${item.fullName}`,
              totalPages: item.totalPages
            }
          }) || []
        }
      )
    )
  }

  getDisciplineTypeList = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let typeList = []
          typeList = resp.items?.find((item: any) => item.code === PARAMETER.DISCIPLINE_TYPE)?.value || []
          typeList = convertStringJSONToArray(typeList)
          return typeList
        }
      )
    )
  }

  getOrgLocalList = (params: any): Observable<any[]> => {
    return this.organizationService.getOrgList({}).pipe(
      map(
        resp => {
          return resp?.items?.map((x: any) => {
            return {
              value: x.id,
              label: x.name
            }
          }) || []
        }
      )
    )
  }

  getPlayerPosition = (params: any): Observable<any[]> => {
    return this.systemParamatersService.getPublic().pipe(
      map(
        resp => {
          let list = []
          list = resp.items?.find((item: any) => item.code === PARAMETER.PLAYER_POSITION)?.value || []
          list = convertStringJSONToArray(list)
          return list
        }
      )
    )
  }
}
